import React, { useState, useEffect, useRef } from 'react';
import { api } from '@/api/apiClient';
import { Link, useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ArrowLeft,
  ArrowRight,
  Heart,
  CheckCircle2,
  FileText,
  PenLine,
  AlertCircle,
  Loader2,
  DollarSign,
  Shield,
  Users
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { calculateEmployeeTaxes } from '@/components/utils/dynamicTaxCalculations';
import { generateEnrollmentPDF, downloadPDF } from '@/components/utils/pdfGeneration';
import { sendEnrollmentConfirmation } from '@/components/email/sendEmail';
import PayStubPreview from '@/components/enrollment/PayStubPreview';
import EnrollmentAIChat from '@/components/ai/EnrollmentAIChat';

const steps = [
  { id: 1, name: 'Review', icon: FileText },
  { id: 2, name: 'Agreement', icon: Shield },
  { id: 3, name: 'Sign', icon: PenLine },
  { id: 4, name: 'Complete', icon: CheckCircle2 },
];

export default function Enrollment() {
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signature, setSignature] = useState('');
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [coverageLevel, setCoverageLevel] = useState('employee_only');
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedFamilyMembers, setSelectedFamilyMembers] = useState([]);
  const [calculatedPremium, setCalculatedPremium] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Persist step for resume
    const persistStep = async () => {
      if (employee?.id) {
        try {
          await api.entities.Employee.update(employee.id, { last_enrollment_step: 'select' });
        } catch (err) {
          console.error('Failed to persist enrollment step', err);
        }
      }
    };
    persistStep();
  }, [employee?.id]);

  // Calculate premium based on coverage level and selected family members
  useEffect(() => {
    if (product) {
      const basePremium = parseFloat(product.base_premium) || 0;
      let multiplier = 1.0;
      
      // Count selected family members by type
      const selectedSpouses = selectedFamilyMembers.filter(id => {
        const member = familyMembers.find(m => m.id === id);
        return member?.relationship === 'spouse';
      }).length;
      
      const selectedChildren = selectedFamilyMembers.filter(id => {
        const member = familyMembers.find(m => m.id === id);
        return member?.relationship === 'child';
      }).length;
      
      // Adjust premium based on coverage level and actual selections
      if (coverageLevel === 'employee_only') {
        multiplier = 1.0;
      } else if (coverageLevel === 'employee_spouse') {
        multiplier = 1.0 + (selectedSpouses * 1.0); // Each spouse adds 100% of base
      } else if (coverageLevel === 'employee_children') {
        multiplier = 1.0 + (selectedChildren * 0.4); // Each child adds 40% of base
      } else if (coverageLevel === 'family') {
        multiplier = 1.0 + (selectedSpouses * 1.0) + (selectedChildren * 0.4);
      }
      
      setCalculatedPremium(basePremium * multiplier);
    }
  }, [product, coverageLevel, selectedFamilyMembers, familyMembers]);

  const loadData = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const productId = urlParams.get('productId');
    
    if (!productId) {
      toast.error('Please choose a benefit to enroll in.');
      navigate(createPageUrl('BenefitsCatalog'));
      setLoading(false);
      return;
    }

    try {
      const user = await api.auth.me();
      
      const [productsData, employees] = await Promise.all([
        api.entities.BenefitProduct.filter({ id: productId }),
        api.entities.Employee.filter({ user_email: user.email })
      ]);

      if (productsData.length > 0) {
        setProduct(productsData[0]);
      } else {
        toast.error('That benefit is no longer available.');
        navigate(createPageUrl('BenefitsCatalog'));
        setLoading(false);
        return;
      }
      if (employees.length > 0) {
        setEmployee(employees[0]);
        // Load family members
        const members = await api.entities.FamilyMember.filter({ 
          employee_id: employees[0].id,
          is_active: true 
        });
        setFamilyMembers(members);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Signature canvas handlers
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.clientX || e.touches?.[0]?.clientX) - rect.left) * scaleX;
    const y = ((e.clientY || e.touches?.[0]?.clientY) - rect.top) * scaleY;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.clientX || e.touches?.[0]?.clientX) - rect.left) * scaleX;
    const y = ((e.clientY || e.touches?.[0]?.clientY) - rect.top) * scaleY;
    
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing && canvasRef.current) {
      setSignature(canvasRef.current.toDataURL());
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature('');
  };

  const handleSubmit = async () => {
    if (!employee || !product) return;
    
    setSubmitting(true);
    try {
      // Calculate tax savings
      let taxSavings = 0;
      try {
        const annualPremium = product.base_premium * 12;
        const taxAnalysis = await calculateEmployeeTaxes(employee, annualPremium);
        taxSavings = taxAnalysis.savings.total;
      } catch {}
      
      // Create enrollment
      const enrollmentData = {
        employee_id: employee.id,
        employer_id: employee.employer_id,
        product_id: product.id,
        status: 'active',
        monthly_premium: parseFloat(calculatedPremium) || 0,
        employee_cost: parseFloat(calculatedPremium) || 0,
        employer_contribution: 0,
        coverage_amount: parseFloat(product.coverage_amount) || 0,
        coverage_level: coverageLevel,
        effective_date: new Date().toISOString(),
        signed_at: new Date().toISOString(),
        signature_data: signature
      };
      
      const newEnrollment = await api.entities.Enrollment.create(enrollmentData);

      // Update employee enrollment status
      await api.entities.Employee.update(employee.id, {
        enrollment_status: 'in_progress'
      });

      // Log the action
      await api.entities.AuditLog.create({
        entity_type: 'Enrollment',
        entity_id: newEnrollment.id,
        action: 'enroll',
        performed_by: employee.user_email,
        notes: `Enrolled in ${product.name}`
      });

      // Generate and download enrollment PDF + send email
      try {
        const employers = await api.entities.Employer.filter({ id: employee.employer_id });
        const familyMembers = await api.entities.FamilyMember.filter({ 
          employee_id: employee.id,
          is_active: true
        });
        const pdf = await generateEnrollmentPDF(newEnrollment, employee, employers[0], product, familyMembers);
        downloadPDF(pdf, `enrollment_${employee.first_name}_${employee.last_name}.pdf`);

        // Send confirmation email
        const emailData = sendEnrollmentConfirmation(employee.user_email, {
          employeeName: `${employee.first_name} ${employee.last_name}`,
          productName: product.name,
          coverageLevel: enrollmentData.coverage_level?.replace('_', ' ') || 'Employee Only',
          monthlyPremium: (enrollmentData.monthly_premium || product.base_premium || 0).toFixed(2),
          coverageAmount: (enrollmentData.coverage_amount || product.coverage_amount || 0).toLocaleString(),
          effectiveDate: format(new Date(enrollmentData.effective_date), 'MMMM d, yyyy'),
          taxSavings: taxSavings > 0 ? taxSavings.toFixed(0) : '0',
          dashboardUrl: window.location.origin + '/EmployeeDashboard',
          employerName: employers[0]?.company_name || 'Your Benefits Team'
        });

        await api.integrations.Core.SendEmail({
          to: emailData.to,
          subject: emailData.subject,
          body: emailData.html
        });
      } catch (pdfErr) {
        console.error('PDF/Email failed:', pdfErr);
      }

      setCurrentStep(4);
      toast.success('Enrollment completed successfully!');
    } catch (err) {
      console.error(err);
      toast.error('Failed to complete enrollment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!product || !employee) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-slate-700 mb-2">Unable to Process Enrollment</h3>
        <p className="text-slate-500 mb-4">Product or employee information not found.</p>
        <Link to={createPageUrl('BenefitsCatalog')}>
          <Button>Back to Catalog</Button>
        </Link>
      </div>
    );
  }

  if (!employee.profile_confirmed) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-slate-700 mb-2">Profile Confirmation Required</h3>
        <p className="text-slate-500 mb-4">Please confirm your profile information before enrolling.</p>
        <Link to={createPageUrl('EmployeeProfile')}>
          <Button className="bg-emerald-500 hover:bg-emerald-600">
            Complete Profile
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Back Button */}
      <Link to={createPageUrl(`BenefitDetail?id=${product.id}`)}>
        <Button variant="ghost" className="text-slate-600">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Benefit Details
        </Button>
      </Link>

      {/* Progress Steps */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isComplete = currentStep > step.id;
              
              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center">
                    <div className={`
                      w-12 h-12 rounded-full flex items-center justify-center transition-all
                      ${isComplete ? 'bg-emerald-500 text-white' : 
                        isActive ? 'bg-emerald-100 text-emerald-600 ring-2 ring-emerald-500 ring-offset-2' : 
                        'bg-slate-100 text-slate-400'}
                    `}>
                      {isComplete ? <CheckCircle2 className="w-6 h-6" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <span className={`mt-2 text-sm font-medium ${isActive ? 'text-emerald-600' : 'text-slate-500'}`}>
                      {step.name}
                    </span>
                  </div>
                  
                  {idx < steps.length - 1 && (
                    <div className={`flex-1 h-1 mx-4 rounded ${isComplete ? 'bg-emerald-500' : 'bg-slate-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Step Content */}
      <AnimatePresence mode="wait">
        {/* Step 1: Review */}
        {currentStep === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Product Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Benefit Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                      <Heart className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-900">{product.name}</h3>
                      <p className="text-slate-600 mt-1">{product.short_description}</p>
                      
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-emerald-600" />
                          <span className="text-slate-600">Monthly Premium:</span>
                          <span className="font-semibold text-lg">${calculatedPremium.toFixed(2)}/mo</span>
                        </div>
                        {product.coverage_amount && (
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-blue-600" />
                            <span className="text-slate-600">Coverage:</span>
                            <span className="font-semibold">${(parseFloat(product.coverage_amount) || 0).toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Coverage Level & Family Members */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Coverage Level
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="coverage-level" className="text-sm font-medium mb-2 block">
                    Who would you like to cover?
                  </Label>
                  <Select value={coverageLevel} onValueChange={setCoverageLevel}>
                    <SelectTrigger id="coverage-level">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee_only">Employee Only</SelectItem>
                      <SelectItem value="employee_spouse">Employee + Spouse</SelectItem>
                      <SelectItem value="employee_children">Employee + Children</SelectItem>
                      <SelectItem value="family">Family (Employee + Spouse + Children)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Show family members if not employee_only */}
                {coverageLevel !== 'employee_only' && familyMembers.length > 0 && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm font-medium text-slate-700 mb-3">Select Family Members to Cover:</p>
                    <div className="space-y-2">
                      {familyMembers.map(member => {
                        const shouldShow = 
                          (coverageLevel === 'employee_spouse' && member.relationship === 'spouse') ||
                          (coverageLevel === 'employee_children' && member.relationship === 'child') ||
                          (coverageLevel === 'family');
                        
                        if (!shouldShow) return null;
                        
                        return (
                          <div key={member.id} className="flex items-center gap-3 p-3 bg-white rounded border">
                            <Checkbox 
                              id={`member-${member.id}`}
                              checked={selectedFamilyMembers.includes(member.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedFamilyMembers([...selectedFamilyMembers, member.id]);
                                } else {
                                  setSelectedFamilyMembers(selectedFamilyMembers.filter(id => id !== member.id));
                                }
                              }}
                            />
                            <Label htmlFor={`member-${member.id}`} className="flex-1 cursor-pointer">
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{member.first_name} {member.last_name}</span>
                                <Badge variant="outline">{member.relationship}</Badge>
                              </div>
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {coverageLevel !== 'employee_only' && familyMembers.length === 0 && (
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      You haven't added any family members yet. <Link to={createPageUrl('FamilyMembers')} className="font-medium underline">Add family members</Link> to include them in your coverage.
                    </p>
                  </div>
                )}

                {/* Cost Summary */}
                <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-xs font-semibold text-emerald-800 mb-3 uppercase tracking-wide">Premium Breakdown</p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Base Premium (Employee):</span>
                      <span className="font-medium">${(parseFloat(product.base_premium) || 0).toFixed(2)}/mo</span>
                    </div>
                    {coverageLevel !== 'employee_only' && selectedFamilyMembers.length > 0 && (
                      <>
                        {selectedFamilyMembers.map(memberId => {
                          const member = familyMembers.find(m => m.id === memberId);
                          if (!member) return null;
                          const basePremium = parseFloat(product.base_premium) || 0;
                          const memberCost = member.relationship === 'spouse' ? basePremium : basePremium * 0.4;
                          return (
                            <div key={memberId} className="flex justify-between text-sm">
                              <span className="text-slate-600 pl-4">+ {member.first_name} ({member.relationship}):</span>
                              <span className="font-medium">${memberCost.toFixed(2)}/mo</span>
                            </div>
                          );
                        })}
                      </>
                    )}
                    <div className="h-px bg-emerald-300 my-2" />
                    <div className="flex justify-between">
                      <span className="font-semibold text-slate-900">Total Monthly Premium:</span>
                      <span className="font-bold text-emerald-600 text-lg">${calculatedPremium.toFixed(2)}/mo</span>
                    </div>
                    {coverageLevel !== 'employee_only' && selectedFamilyMembers.length === 0 && (
                      <p className="text-xs text-slate-500 italic mt-2">Select family members above to see updated cost</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pay Stub Preview */}
            <PayStubPreview 
              employee={employee} 
              monthlyPremium={calculatedPremium} 
            />

            {/* Employee Info */}
            <Card>
              <CardHeader>
                <CardTitle>Your Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Employee Name</p>
                    <p className="font-medium">{employee.first_name} {employee.last_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium">{employee.user_email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Effective Date</p>
                    <p className="font-medium">{format(new Date(), 'MMMM d, yyyy')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <Badge className="bg-amber-100 text-amber-700">Pending Enrollment</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={() => setCurrentStep(2)} className="bg-emerald-500 hover:bg-emerald-600">
                Continue to Agreement
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Agreement */}
        {currentStep === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <Card>
              <CardHeader>
                <CardTitle>Terms and Conditions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="h-64 overflow-y-auto p-4 bg-slate-50 rounded-lg text-sm text-slate-600 space-y-4">
                  <h4 className="font-semibold text-slate-800">BENEFIT ENROLLMENT AGREEMENT</h4>
                  <p>By enrolling in this benefit plan, I acknowledge and agree to the following terms and conditions:</p>
                  
                  <p><strong>1. Accuracy of Information:</strong> I certify that all information provided in this enrollment is true and accurate to the best of my knowledge. I understand that any false or misleading information may result in denial of claims or termination of coverage.</p>
                  
                  <p><strong>2. Premium Deductions:</strong> I authorize the deduction of applicable premiums from my paycheck as specified in this enrollment.</p>
                  
                  <p><strong>3. Coverage Effective Date:</strong> I understand that coverage will become effective on the first of the month following enrollment approval.</p>
                  
                  <p><strong>4. Changes and Cancellations:</strong> I understand that changes to my coverage may only be made during the annual open enrollment period or following a qualifying life event.</p>
                  
                  <p><strong>5. Privacy:</strong> I consent to the collection and use of my personal information for the purpose of administering my benefits.</p>
                  
                  <p><strong>6. Electronic Records:</strong> I agree to receive benefit-related communications electronically.</p>
                </div>

                <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-lg">
                  <Checkbox 
                    id="terms" 
                    checked={agreedToTerms}
                    onCheckedChange={setAgreedToTerms}
                    className="mt-1"
                  />
                  <Label htmlFor="terms" className="text-slate-700 cursor-pointer">
                    I have read and agree to the terms and conditions. I understand that this is a legally binding agreement.
                  </Label>
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setCurrentStep(1)}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    onClick={() => setCurrentStep(3)} 
                    disabled={!agreedToTerms}
                    className="bg-emerald-500 hover:bg-emerald-600"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Step 3: Signature */}
        {currentStep === 3 && (
          <motion.div
            key="step3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <Card>
              <CardHeader>
                <CardTitle>Digital Signature</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-slate-600">Please sign below to complete your enrollment.</p>
                
                <div className="border-2 border-dashed border-slate-300 rounded-xl p-2 bg-white">
                  <canvas
                    ref={canvasRef}
                    width={600}
                    height={200}
                    className="w-full touch-none cursor-crosshair"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                </div>
                
                <div className="flex justify-center">
                  <Button variant="outline" size="sm" onClick={clearSignature}>
                    Clear Signature
                  </Button>
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setCurrentStep(2)}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    onClick={handleSubmit} 
                    disabled={!signature || submitting}
                    className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Complete Enrollment
                        <CheckCircle2 className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Step 4: Complete */}
        {currentStep === 4 && (
          <motion.div
            key="step4"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-12"
          >
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>
            
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">Enrollment Complete!</h2>
            <p className="text-slate-600 mb-8 max-w-md mx-auto">
              Congratulations! You have successfully enrolled in <strong>{product.name}</strong>. 
              Your coverage will begin on {format(new Date(), 'MMMM d, yyyy')}.
            </p>
            
            <div className="flex justify-center gap-4">
              <Link to={createPageUrl('MyBenefits')}>
                <Button variant="outline">
                  View My Benefits
                </Button>
              </Link>
              <Link to={createPageUrl('EmployeeDashboard')}>
                <Button className="bg-emerald-500 hover:bg-emerald-600">
                  Back to Dashboard
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Chat Assistant */}
      <EnrollmentAIChat 
        context={{ 
          currentPage: 'enrollment',
          currentStep: currentStep,
          product: product ? {
            name: product.name,
            description: product.short_description,
            premium: parseFloat(product.base_premium) || 0,
            coverage: parseFloat(product.coverage_amount) || 0
          } : null,
          coverageLevel: coverageLevel,
          familyMembersCount: selectedFamilyMembers.length,
          enrollmentInProgress: true
        }} 
      />
    </div>
  );
}